import { useState } from "react";
import { ArrowRight, Car, Check } from "lucide-react";
import { type FuelType, type ManualVehicle } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { Plate } from "../../components/Plate";
import { SelectField, TextField } from "../../components/Field";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import type { StepProps } from "../stepTypes";
import s from "../steps.module.css";

const FUELS: FuelType[] = ["Petrol", "Diesel", "Hybrid", "Electric", "Other"];
const YEARS = Array.from(
  { length: 36 },
  (_, i) => new Date().getFullYear() + 1 - i,
);

/** Step 2 — confirm the looked-up vehicle, or capture it manually. */
export function VehicleStep({ onNext, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const vehicle = useSession((st) => st.vehicle);
  const vehicleFound = useSession((st) => st.vehicleFound);
  const registration = useSession((st) => st.vehicleRegistration);
  const setVehicle = useSession((st) => st.setVehicle);

  // If a found vehicle exists, show the confirm card; otherwise manual form.
  const isConfirm = !!vehicle && vehicleFound;

  if (isConfirm && vehicle) {
    const v = vehicle as {
      make: string;
      model: string;
      year?: number;
      fuel?: string;
      colour?: string;
    };
    return (
      <div>
        <StepHeader kicker="Your vehicle" title="Is this your car?" />
        {config.sandbox && (
          <span className={s.foundBadge}>
            <Check size={13} /> Found on the DVLA register
          </span>
        )}
        <div className={s.vehicleCard}>
          <span className={s.vehicleIcon}>
            <Car size={28} strokeWidth={1.6} />
          </span>
          <div style={{ minWidth: 0 }}>
            <p className={s.vehicleName}>
              {v.make} {v.model}
            </p>
            <div className={s.vehicleMeta}>
              {v.year && <span className={s.metaChip}>{v.year}</span>}
              {v.fuel && <span className={s.metaChip}>{v.fuel}</span>}
              {v.colour && <span className={s.metaChip}>{v.colour}</span>}
            </div>
          </div>
        </div>
        {registration && (
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 18 }}
          >
            <Plate registration={registration} size="lg" />
          </div>
        )}
        <div className={s.actions}>
          <Button
            block
            trailingIcon={<ArrowRight size={18} />}
            onClick={() => {
              saveLead("vehicle");
              onNext();
            }}
          >
            Yes, that&rsquo;s my car
          </Button>
          {config.sandbox && (
            <Button
              variant="ghost"
              block
              onClick={() => setVehicle(null, { found: false })}
            >
              No, enter details manually
            </Button>
          )}
        </div>
      </div>
    );
  }

  return config.sandbox ? (
    <ManualVehicleForm onNext={onNext} saveLead={saveLead} />
  ) : null;
}

function ManualVehicleForm({
  onNext,
  saveLead,
}: Pick<StepProps, "onNext" | "saveLead">) {
  const config = useSession((st) => st.config)!;
  const existing = useSession(
    (st) => st.vehicle,
  ) as Partial<ManualVehicle> | null;
  const setVehicle = useSession((st) => st.setVehicle);

  const [make, setMake] = useState(existing?.make ?? "");
  const [model, setModel] = useState(existing?.model ?? "");
  const [fuel, setFuel] = useState<string>(existing?.fuel ?? "");
  const [year, setYear] = useState<string>(
    existing?.year ? String(existing.year) : "",
  );
  const [touched, setTouched] = useState(false);

  const errors = {
    make: !make.trim() ? "Please enter the make" : null,
    model: !model.trim() ? "Please enter the model" : null,
    fuel: !fuel ? "Select a fuel type" : null,
    year: !year ? "Select a year" : null,
  };
  const valid = !errors.make && !errors.model && !errors.fuel && !errors.year;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    const v: ManualVehicle = {
      make: make.trim(),
      model: model.trim(),
      fuel: fuel as FuelType,
      year: Number(year),
    };
    setVehicle(v, { found: false });
    track("manual_vehicle_entered", {
      make: v.make,
      model: v.model,
      fuel: v.fuel,
    });
    saveLead("vehicle");
    onNext();
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeader
        kicker="Your vehicle"
        title={config.copy.vehicleNotFoundTitle}
        subtitle="A few details so we can give you the best price."
        display={false}
      />
      <div className={s.stack}>
        <div className={s.actionsRow}>
          <TextField
            label="Make"
            placeholder="e.g. Volkswagen"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            error={touched ? errors.make : null}
            autoComplete="off"
          />
          <TextField
            label="Model"
            placeholder="e.g. Golf"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            error={touched ? errors.model : null}
            autoComplete="off"
          />
        </div>
        <div className={s.actionsRow}>
          <SelectField
            label="Fuel type"
            value={fuel}
            onChange={(e) => setFuel(e.target.value)}
            error={touched ? errors.fuel : null}
          >
            <option value="" disabled>
              Select fuel
            </option>
            {FUELS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </SelectField>
          <SelectField
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            error={touched ? errors.year : null}
          >
            <option value="" disabled>
              Select year
            </option>
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </SelectField>
        </div>
      </div>
      <div className={s.actions}>
        <Button type="submit" block trailingIcon={<ArrowRight size={18} />}>
          {config.copy.ctaContinue}
        </Button>
      </div>
    </form>
  );
}
